import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChangeLog, Pagination, Response } from '@ui/src';
import {
  PermissionEnum,
  PermissionsAction,
  RoleDetails,
  RoleValidator,
} from '../../types';
import sectionActions from '../../all-sections';
import { userRolesApi } from '../services/roles';

export interface RolesColumnState {
  roles?: RoleDetails[];
  selectedRole?: RoleDetails;
  panelIsOpen: boolean;
  roleInfo?: RoleDetails;
  roleId?: string;
  sectionId: PermissionEnum;
  pageId?: PermissionEnum | 'all';
  validators?: RoleValidator;
  search: string;
  allActionsSectionFiltered?: PermissionsAction;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
}

const initialState: RolesColumnState = {
  panelIsOpen: false,
  search: '',
  sectionId: PermissionEnum.SECTION_DASHBOARD,
  allActionsSectionFiltered: {},
};

export const rolesColumnSlice = createSlice({
  name: 'rolesColumn',
  initialState,
  reducers: {
    onRoleChange: (state, action: PayloadAction<string>) => {
      const selectedRole =
        state.roles?.find((item) => item.id === action.payload) || undefined;

      state.selectedRole = selectedRole;
      state.roleId = action.payload;
    },
    onSectionChange: (state, action: PayloadAction<PermissionEnum>) => {
      state.sectionId = action.payload;
      state.allActionsSectionFiltered = action.payload
        ? sectionActions[action.payload]
        : {};
      state.search = '';
    },
    onPageChange: (
      state,
      action: PayloadAction<PermissionEnum | undefined>,
    ) => {
      state.pageId = action.payload;
    },
    onFilterChange: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },
    onFilterChangeSuccess: (
      state,
      action: PayloadAction<PermissionsAction | undefined>,
    ) => {
      state.allActionsSectionFiltered = action.payload;
    },
    changePanelState: (state, action: PayloadAction<boolean>) => {
      state.panelIsOpen = action.payload;
      state.changeLogs = undefined;
      state.changeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        userRolesApi.endpoints.getRoles.matchFulfilled,
        (state, action: PayloadAction<Response<RoleDetails>>) => {
          const nextRoles =
            action.payload.data.edges.map((item) => item.node) || [];

          state.roles = nextRoles;

          if (nextRoles && nextRoles[0]) {
            state.selectedRole = nextRoles[0] || undefined;
            state.roleId = nextRoles[0].id;
          }
        },
      )
      .addMatcher(
        userRolesApi.endpoints.getRole.matchFulfilled,
        (state, action: PayloadAction<{ data: RoleDetails }>) => {
          state.roleInfo = action.payload.data;
        },
      )
      .addMatcher(
        userRolesApi.endpoints.getRoleChangeLogs.matchFulfilled,
        (
          state,
          action: PayloadAction<Response<ChangeLog>> & {
            meta: { arg: { originalArgs: string } };
          },
        ) => {
          if (!action.meta.arg.originalArgs.includes('cursor')) {
            state.changeLogs = action.payload.data?.edges?.map(
              (item) => item.node,
            );
          } else {
            const nextChangeLogs = ([] as ChangeLog[]).concat(
              state.changeLogs as ChangeLog[],
            );

            action.payload.data.edges?.forEach((item) => {
              nextChangeLogs.push(item.node);
            });

            state.changeLogs = nextChangeLogs;
          }

          state.changeLogsPagination = action.payload.data?.pageInfo;
        },
      )
      .addMatcher(
        userRolesApi.endpoints.editRole.matchFulfilled,
        (state, action: PayloadAction<{ data: RoleDetails }>) => {
          const nextRoles = state?.roles?.map((item) => {
            if (item.id === action.payload.data.id) {
              return { ...action.payload.data, usersCount: item.usersCount };
            }
            return { ...item };
          });
          state.roles = [...(nextRoles as RoleDetails[])];
        },
      )
      .addMatcher(
        userRolesApi.endpoints.getRoleValidators.matchFulfilled,
        (state, action: PayloadAction<{ data: RoleValidator }>) => {
          state.validators = action.payload.data;
        },
      );
  },
});

export const {
  onRoleChange,
  onSectionChange,
  onPageChange,
  changePanelState,
  onFilterChange,
  onFilterChangeSuccess,
} = rolesColumnSlice.actions;

export default rolesColumnSlice.reducer;
